import React from 'react'

import {
  Header,
  Layout,
  Code,
  SectionTitle,
  ItemTitle,
  Disclaimer,
  Intro,
  NavTabs,
  NavTabsItem,
} from '../../../../components'

import data from './data.json'

export default () => {
  return (
    <>
      <Header
        slug={data.slug + '/develop/'}
        site={data.site}
        state="develop"
        title={data.title}
      />
      <Layout
        subtitle={data.subtitle}
        slug={data.slug + '/develop/'}
        site={data.site}
        state="develop"
        title="Start Developing"
        complete={true}
      >
        <Intro noBorder={true}>
          <p>
            Currency’s code library provides developers with out of the box
            tools to create a consistent user experience across products, while
            also providing flexibility for ad hoc decision-making. Using the
            library as intended lets developers work faster while following SCSS
            and HTML conventions.
          </p>
        </Intro>
        <NavTabs>
          <NavTabsItem anchor="guidelines" active={true}>
            Guideline &amp; Best Practices
          </NavTabsItem>
          <NavTabsItem anchor="implementation">Implementation</NavTabsItem>
          <NavTabsItem anchor="install">Installing Currency</NavTabsItem>
          <NavTabsItem anchor="scss">Usage</NavTabsItem>
          <NavTabsItem anchor="customize">Customizing Currency</NavTabsItem>
        </NavTabs>
        <SectionTitle anchor="guidelines">
          Guidelines and Best Practices
        </SectionTitle>
        <h2 class="misc-title">Markup and Style</h2>
        <ItemTitle class="l-margin-top--small">Namespaces</ItemTitle>
        <p>
          Currency classes are preceded by namespaces that signify their use.
        </p>
        <p class="u-type--small">
          <span class="inline__code">.c-</span> Components
        </p>
        <p class="u-type--small">
          <span class="inline__code">.p-</span> Patterns
        </p>
        <p class="u-type--small">
          <span class="inline__code">.l-</span> Layouts
        </p>
        <p class="u-type--small">
          <span class="inline__code">.u-</span> Utilities
        </p>
        <ItemTitle>BEM Naming</ItemTitle>
        <p>
          In order to keep our code base manageable and specificity low,
          Currency follows the BEM (Block, Element, Modifier) naming convention.
          A block represents an independent component that is functional on its
          own. All variants and properties of that component are included in the
          base class.
        </p>
        <div class="inline__code">.c-card</div>
        <p>
          An element, a part of the base class, is separated from the base name
          by two underscores. An element is dependent on the block for meaning.
        </p>
        <p>
          <div class="inline__code">.c-card__img</div>
        </p>
        <p>
          <div class="inline__code">.c-card__title </div>
        </p>
        <p>
          <div class="inline__code">.c-card__body</div>
        </p>
        <p>
          A modifier changes the base class or parent element and is separated
          from the element by one dash. It’s used to change an element’s
          appearance or state.
        </p>
        <div class="inline__code">.c-button--primary-disabled</div>
        <h2 class="misc-title">Accessibility</h2>
        <p>
          A benefit of using a centralized design system is ensuring that all of
          our users, regardless of ability, have a phenomenal experience using
          any of Wilmington Trust’s products. Currency follows the{' '}
          <strong>POUR</strong> standard set forth by the{' '}
          <a
            href="https://www.w3.org/TR/WCAG21/"
            target="_blank"
            rel="noopener noreferrer"
            class="link"
          >
            Web Content Accessibility Guidelines (WCAG)
          </a>
          : we design experiences that are <strong>perceivable</strong>,{' '}
          <strong>operable</strong>, <strong>understandable</strong>, and{' '}
          <strong>robust</strong>. And while accessibility is baked into our
          components, collaboration between all members of product teams is
          essential.
        </p>
        <hr />
        <SectionTitle anchor="implementation">Implementation</SectionTitle>
        <p>
          Currency code assets are available in our Azure DevOps repository
          which is available to clone for use. For questions regarding
          permissions restrictions, please reach out to the Currency team.
        </p>
        <hr />
        <SectionTitle anchor="install">Installing Currency</SectionTitle>
        <p>
          Currency is hosted on M&T's private Artficatory registry and can be
          installed via npm.
        </p>
        <ItemTitle>Set your registry</ItemTitle>
        <p>Set your npm registry to Artficatory.</p>
        <Code site={data.site} language="bash">
          npm config set registry
          https://bar.prod.mtb.com/artifactory/api/npm/mtb-npm-ga
        </Code>
        <Disclaimer>
          If you do not have access to Artifactory, please reach out your tech
          lead for the appropriate permissions.
        </Disclaimer>
        <ItemTitle>Install the package</ItemTitle>
        <p>Install Currency as an npm dependency.</p>
        <Code site={data.site} language="bash">
          npm install currency-design-system --save-dev
        </Code>
        <ItemTitle>Updating Currency</ItemTitle>
        <p>
          The Currency team will push all updates to Currency to the npm package
          upon every release. Run the following command to update to the latest
          version available on Artifactory:
        </p>
        <Code site={data.site} language="bash">
          npm update currency-design-system
        </Code>
        <hr />
        <SectionTitle anchor="scss">Usage</SectionTitle>
        <p>
          Currency can be used in its entirety or broken down to smaller pieces
          to fit the needs of your project.
        </p>
        <ItemTitle>Import main SCSS file</ItemTitle>
        <p>To use all of Currency, simply import the following file:</p>
        <Code site={data.site} language="scss">
          @import
          "~currency-design-system/scss/mtb-currency-design--defaults-set.scss";
        </Code>
        <p>
          Use the below import if you do not wish to have your defaults
          overwritten:
        </p>
        <Code site={data.site} language="scss">
          @import "~currency-design-system/scss/mtb-currency-design.scss";
        </Code>
        <ItemTitle>Import main CSS file</ItemTitle>
        <p>
          If your project does not use SCSS/SASS, or if you do not intend to
          modify Currency, import the following pre-compiled stylesheet:
        </p>
        <Code site={data.site} language="scss">
          @import
          "~currency-design-system/css/mtb-currency-design--defaults-set.css";
        </Code>
        <p>
          Use the below import if you do not wish to have your defaults
          overwritten:
        </p>
        <Code site={data.site} language="scss">
          @import "~currency-design-system/scss/mtb-currency-design.scss";
        </Code>
        <hr />
        <SectionTitle anchor="customize">Customizing Currency</SectionTitle>
        <p>
          If you have Currency installed in your{' '}
          <span class="inline__code">node_modules</span> folder, you can choose
          to import select stylesheets to fit the needs of your project, as
          shown in the example below:
        </p>
        <Code site={data.site} language="scss">
          {code.all}
        </Code>
        <ItemTitle>Local Paths</ItemTitle>
        <div class="c-notification--inline-warning l-margin-bottom-xlarge">
          <div class="c-notification__icon">
            <i class="icon icon--exclamation"></i>
          </div>
          <div class="c-notification__content">
            By default, Currency will search for the following assets in your
            node_modules directory. To host them locally, you may change the
            following variables to your new path:
          </div>
        </div>

        <Code site={data.site} language="scss">
          {code.localPaths}
        </Code>

        <ItemTitle>Tokens &amp; Settings</ItemTitle>
        <p>
          This step is <strong>mandatory</strong> in order for Currency to work,
          as the other files depend on it.
        </p>
        <Code site={data.site} language="scss">
          {code.tokens}
        </Code>
        <Disclaimer>
          This imports a function that creates mapping for theme to local
          variables.
        </Disclaimer>
        <ItemTitle>Tools</ItemTitle>
        <p>
          This step is mandatory in order for Currency to work, as the other
          files depend on it.
        </p>
        <Code site={data.site} language="scss">
          @import "2-tools/__tools-index";
        </Code>
        <Disclaimer>
          Preprocessor functions to help write or extend properties on classes.
          This includes: functions, placeholders, mixins and media queries.
        </Disclaimer>
        <ItemTitle>Vendors</ItemTitle>
        <p>
          Third-party styles that are being used on a project. ie Bootstrap or
          Foundation.
        </p>
        <Code site={data.site} language="scss">
          @import '3-vendors/__vendor-index';
        </Code>
        <ItemTitle>Elements</ItemTitle>
        <Code site={data.site} language="scss">
          @import "4-defaults/__defaults-index";
        </Code>
        <Disclaimer>
          Elements are HTML native that are only meant to be used as a
          standalone element and should not be used to compose components.
        </Disclaimer>
        <ItemTitle>Components</ItemTitle>
        <p>
          Classes begin with a namespace: <span class="inline__code">c-</span>
        </p>
        <Code site={data.site} language="scss">
          @import "5-components/__component-index";
        </Code>
        <Disclaimer>
          Components are small features (ex. buttons) that make up a part of the
          website. Each component is fully functional by itself and does not
          rely on any other components.
        </Disclaimer>
        <ItemTitle>Patterns</ItemTitle>
        <p>
          Classes begin with a namespace: <span class="inline__code">p-</span>
        </p>
        <Code site={data.site} language="scss">
          @import "6-patterns/__pattern-index";
        </Code>
        <Disclaimer>
          Patterns are combinations of components that are not saleable such as
          a header, footer or modal. Only one pattern should appear on a page at
          once.
        </Disclaimer>
        <ItemTitle>Layouts</ItemTitle>
        <p>
          Classes begin with a namespace: <span class="inline__code">l-</span>
        </p>
        <Code site={data.site} language="scss">
          @import '7-layouts/__layout-index';
        </Code>
        <Disclaimer>
          Layouts are used for design patterns where items are arranged (not
          decorated). When you make a change to a layout class, every page
          throughout the website will be affected.
        </Disclaimer>
        <ItemTitle>Utilities</ItemTitle>
        <p>
          Classes begin with a namespace: <span class="inline__code">u-</span>
        </p>
        <Code site={data.site} language="scss">
          @import "9-utils/__util-index";
        </Code>
        <Disclaimer>
          Make changes only for a certain style in a specific place. Utility
          classes can help us update it without having to change the entire CSS
          structure.
        </Disclaimer>
        <hr />
      </Layout>
    </>
  )
}

export const code = {
  linkTag: `<link rel="stylesheet" href="https://cdn.mtb.com/currency/currency.min.css" />`,
  localPaths: `$_localFontPath: "~currency-design-system/fonts/"; 
$_arrowPath: "~currency-design-system/images/dropdownArrow.svg";
$_fillPath: "~currency-design-system/images/dropdownFill.svg";`,
  tokens: `@import "1-settings/brand/mt/__var-initials";
        "1-settings/brand/mt/mt-tokens";
        "1-settings/tokens.scss";
        "1-settings/theming/theme-logic", 
        "1-settings/__var-index";`,
  all: `$_localFontPath: "~currency-design-system/fonts/";
$_arrowPath: "~currency-design-system/images/dropdownArrow.svg";
$_fillPath: "~currency-design-system/images/dropdownFill.svg";

@import "~currency-design-system/scss/1-settings/brand/mt/__var-initials",
        "~currency-design-system/scss/1-settings/brand/mt/mt-tokens",
        "~currency-design-system/scss/1-settings/tokens.scss",
        "~currency-design-system/scss/1-settings/theming/_theme-logic",
        "~currency-design-system/scss/1-settings/brand/mt/__var-index",
        "~currency-design-system/scss/2-tools/__tools-index",
        "~currency-design-system/scss/4-defaults/__defaults-index",
        "~currency-design-system/scss/5-components/__component-index",
        "~currency-design-system/scss/6-patterns/__pattern-index",
        "~currency-design-system/scss/7-layouts/__layout-index",
        "~currency-design-system/scss/9-utils/__util-index";
`,
}
